import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import styles from "./ServiceOrderDescription.module.css";
import generalStyles from "src/screens/tickets/ticket/details/actions/assignOrder/AssignOrderModal.module.css";
import { RootStoreContext } from "src/stores/RootStore";
import { useTranslation } from "react-i18next";
import { ServiceProviderListItem } from "src/screens/tickets/ticket/details/actions/assignOrder/selectServiceProvider/serviceProviderListItem/ServiceProviderListItem";
import { BILLING_DATA_PROPERTY, ORDER_CREATION_STATUS } from "src/stores/tickets/AssignOrderModalStore";
import { ImageCheckboxGrid } from "src/screens/tickets/ticket/details/actions/assignOrder/ImageCheckbox/ImageCheckboxGrid";
import { GetTicketByNumber_primarycontacttypeEnum } from "src/api/generated/GetTicketByNumber";
import { ColorStyle } from "src/utils/Colors";
import { ContactForm } from "./contactForm/ContactForm";
import { GetPersonsWithoutExternal_persons } from "src/api/generated/GetPersonsWithoutExternal";
import { runInAction } from "mobx";

interface SortItem {
    value: string;
    label: string;
}

const highlightText = (text: string, query: string) => {
    if (!query) return text; // Return the original text if no query

    // Create a regex to match the query, ignoring case
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => (
        <span
            key={index}
            style={part.toLowerCase() === query.toLowerCase() ? { fontWeight: "bold", color: "#333" } : {}}
        >
            {part}
        </span>
    ));
};

export const ServiceOrderDescriptionBase = () => {
    const { assignOrderModalStore, ticketStore } = useContext(RootStoreContext);

    useEffect(() => {
        const loadIfNeeded = async () => {
            // Load enumeration if not already loaded or empty
            if (!ticketStore.primarycontacttypeEnum || ticketStore.primarycontacttypeEnum.length === 0) {
                await ticketStore.loadEnumerations();
            }

            if (
                assignOrderModalStore.filteredIncidentPersons.length > 0 &&
                ticketStore.primarycontacttypeEnum.length > 0
            ) {
                const person = assignOrderModalStore.filteredIncidentPersons[0];
                const roleLabel = ticketStore.primarycontacttypeEnum[0]?.label || "";

                assignOrderModalStore.setPrimaryContactDescription(
                    assignOrderModalStore.getPrimaryContactDescription(person, roleLabel)
                );

                assignOrderModalStore.setPrimarycontact(person);
                assignOrderModalStore.setPrimarycontacttype(ticketStore.primarycontacttypeEnum[0].key!);
            }
        };

        loadIfNeeded();
    }, [ticketStore.primarycontacttypeEnum, assignOrderModalStore, ticketStore]);

    return (
        <>
            {assignOrderModalStore.currentOrderCreationStatus === ORDER_CREATION_STATUS.INIT ? (
                <>
                    <UI.Row className={"mt-2"}>
                        <UI.Col xs={12}>
                            <Header />
                        </UI.Col>
                    </UI.Row>

                    <UI.Row>
                        <UI.Col className="mt-4">
                            <OrderText />
                        </UI.Col>
                    </UI.Row>

                    <ImageCheckboxGrid />

                    <UI.Row>
                        <UI.Col className="mt-4 mb-4">
                            <BillingDetails />
                        </UI.Col>
                    </UI.Row>

                    <UI.Row>
                        <UI.Col className="mt-4 mb-4">
                            <div className={styles.ContactsContainer}>
                                <ContactDetailsTitle />
                                <div
                                    className={`${styles.ContactsForm} ${
                                        assignOrderModalStore.isContactPersonsCollapsed ? "" : styles.Collapsed
                                    }`}
                                >
                                    <UI.Row className="mt-2">
                                        <UI.Col className={styles.ContactDropdown} md={4}>
                                            <ContactDetailsContactDropdown />
                                        </UI.Col>
                                        <ContactDetailsPersonSearch />
                                    </UI.Row>
                                    <ContactDetailsContactForm />
                                </div>
                                {assignOrderModalStore.isContactPersonsCollapsed ? undefined : (
                                    <div>
                                        {"\u00A0\u00A0"}
                                        {assignOrderModalStore.primarycontactDescription}
                                    </div>
                                )}
                            </div>
                        </UI.Col>
                    </UI.Row>
                    <Buttons />
                </>
            ) : undefined}
            <CreateOrderProgress />
        </>
    );
};

const HeaderBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const onClickBack = useCallback(() => {
        assignOrderModalStore.moveToPrevProcessStep();
    }, [assignOrderModalStore]);

    return (
        <ServiceProviderListItem
            serviceProvider={assignOrderModalStore.selectedServiceProvider!}
            onClickServiceProviderButton={onClickBack}
            serviceProviderItemButtonLabel={t("labels.change")}
        />
    );
};
const Header = observer(HeaderBase);

const OrderTextBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const onChangeOrderText = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newServiceProviderOrderDescriptionText = e.target.value;

            assignOrderModalStore.setServiceProviderOrderDescription(newServiceProviderOrderDescriptionText);
        },
        [assignOrderModalStore]
    );

    const hasEmailAddress =
        assignOrderModalStore.selectedServiceProvider?.email &&
        assignOrderModalStore.selectedServiceProvider.email.length > 0;

    if (hasEmailAddress) {
        return (
            <UI.Input
                label={`${t("screens.tickets.modals.assign_order.order_text").toString()} *`}
                type="text"
                as="textarea"
                autoComplete="off"
                value={assignOrderModalStore.serviceProviderOrderDescription}
                onChange={onChangeOrderText}
                errorMsg={assignOrderModalStore.errors.serviceOrderDescription ?? undefined}
                className={styles.DescriptionTextArea}
            />
        );
    }
    return (
        <p className={styles.ErrorText}>
            {t("screens.tickets.modals.assign_order.service_provider_missing_email").toString()}
        </p>
    );
};
const OrderText = observer(OrderTextBase);

const BillingDetailsBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const toggleIsEditingBillingData = useCallback(() => {
        assignOrderModalStore.setIsEditingBillingData(!assignOrderModalStore.isEditingBillingData);
    }, [assignOrderModalStore]);

    const onChangeBillingData = useCallback(
        (billingDataProperty: BILLING_DATA_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (assignOrderModalStore.billingData) {
                assignOrderModalStore.updateBillingDataProperty(billingDataProperty, newValue);
            }
        },
        [assignOrderModalStore]
    );

    return (
        <div className={styles.BillingDetailsContainer}>
            <div
                className={`${styles.BillingDetailsTitleContainer}  ${
                    assignOrderModalStore.isEditingBillingData ? "" : styles.Collapsed
                }`}
                onClick={toggleIsEditingBillingData}
            >
                <div className={styles.BillingDetailsTitle}>{t("screens.tickets.form.billing.information")}</div>
                <button />
            </div>
            <div
                className={`${styles.BillingDetailsForm} ${
                    assignOrderModalStore.isEditingBillingData ? "" : styles.Collapsed
                }`}
            >
                <UI.Row>
                    <UI.Col lg={5}>
                        <UI.Input
                            label={t("screens.tickets.form.person.name").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.name ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.NAME)}
                        />
                    </UI.Col>
                    <UI.Col lg={5}>
                        <UI.Input
                            label={t("screens.tickets.form.person.street").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.street ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.STREET)}
                        />
                    </UI.Col>
                    <UI.Col lg={2}>
                        <UI.Input
                            label={t("screens.tickets.form.person.street_number").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.housenumber ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.HOUSENUMBER)}
                        />
                    </UI.Col>
                </UI.Row>
                <UI.Row>
                    <UI.Col lg={5}>
                        <UI.Input
                            label={t("screens.tickets.form.person.co").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.co ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.CO)}
                        />
                    </UI.Col>
                    <UI.Col lg={3}>
                        <UI.Input
                            label={t("screens.tickets.form.person.zip").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.zip ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.ZIP)}
                        />
                    </UI.Col>
                    <UI.Col lg={4}>
                        <UI.Input
                            label={t("screens.tickets.form.person.city").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.city ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.CITY)}
                        />
                    </UI.Col>
                </UI.Row>
                <UI.Row>
                    <UI.Col lg={5}>
                        <UI.Input
                            label={t("screens.tickets.form.billing.bill_email").toString()}
                            type="text"
                            autoComplete="off"
                            value={assignOrderModalStore.billingData.email ?? ""}
                            onChange={onChangeBillingData(BILLING_DATA_PROPERTY.EMAIL)}
                        />
                    </UI.Col>
                </UI.Row>
            </div>
            <div className={`${styles.BillingDetails} ${assignOrderModalStore.isEditingBillingData ? "d-none" : ""}`}>
                {"\u00A0\u00A0"}
                {`${assignOrderModalStore.billingData.name}, ${t("screens.tickets.form.person.co")}, ${
                    assignOrderModalStore.billingData.co
                }...${assignOrderModalStore.billingData.email ? `, ${assignOrderModalStore.billingData.email}` : ""}`}
            </div>
        </div>
    );
};
const BillingDetails = observer(BillingDetailsBase);

const ContactDetailsTitleBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const toggleContactPersonData = useCallback(() => {
        assignOrderModalStore.setIsContactPersonsCollapsed(!assignOrderModalStore.isContactPersonsCollapsed);
    }, [assignOrderModalStore]);

    return (
        <div
            className={`${styles.BillingDetailsTitleContainer}  ${
                assignOrderModalStore.isContactPersonsCollapsed ? "" : styles.Collapsed
            }`}
            onClick={toggleContactPersonData}
        >
            <div className={styles.BillingDetailsTitle}>{t("screens.tickets.form.primarycontact.description")}</div>
            <button />
        </div>
    );
};
const ContactDetailsTitle = observer(ContactDetailsTitleBase);

const ContactDetailsContactFormBase = () => {
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const onClickBinIcon = useCallback(
        () => async (e: any) => {
            assignOrderModalStore.setIsOtherPersonSelected(true);
            assignOrderModalStore.setIsPersonSelectedFromList(false);
            assignOrderModalStore.setPrimarycontact({ name1: "" });
            assignOrderModalStore.setPrimaryContactDescription("");
        },
        [assignOrderModalStore]
    );

    if (assignOrderModalStore.isOtherPersonSelected && assignOrderModalStore.currentPersonSearchQuery.length <= 0) {
        return <ContactForm />;
    }

    return (
        <div className={styles.ServiceProviderListItem} key={assignOrderModalStore.primarycontact?.name1 || ""}>
            <div className={styles.ServiceProviderListRow}>
                <div className="font-weight-bold">{`${assignOrderModalStore.primarycontact?.name1} ${assignOrderModalStore.primarycontact?.name2}`}</div>
            </div>
            <div className={styles.ServiceProviderListRowTwo}>
                {/* Check for phone first; if phone exists, use it, otherwise check phoneprivate */}
                {assignOrderModalStore.primarycontact?.phone ? (
                    <div className={styles.ServiceProviderListRowIconItem}>
                        <UI.Icon icon={UI.SVGIcon.PhoneOutlined} size={25} color={ColorStyle("grey")} />
                        <span>{assignOrderModalStore.primarycontact.phone}</span>
                    </div>
                ) : assignOrderModalStore.primarycontact?.phoneprivate ? (
                    <div className={styles.ServiceProviderListRowIconItem}>
                        <UI.Icon icon={UI.SVGIcon.PhoneOutlined} size={25} color={ColorStyle("grey")} />
                        <span>{assignOrderModalStore.primarycontact.phoneprivate}</span>
                    </div>
                ) : undefined}
                {assignOrderModalStore.primarycontact?.mobile ? (
                    <div className={styles.ServiceProviderListRowIconItem}>
                        <UI.Icon icon={UI.SVGIcon.Device} size={25} color={ColorStyle("grey")} />
                        <span>{assignOrderModalStore.primarycontact.mobile}</span>
                    </div>
                ) : undefined}
                {assignOrderModalStore.primarycontact?.email ? (
                    <div className={styles.ServiceProviderListRowIconItem}>
                        <UI.Icon icon={UI.SVGIcon.Mail} size={25} color={ColorStyle("grey")} />
                        <span>{assignOrderModalStore.primarycontact.email}</span>
                    </div>
                ) : undefined}
            </div>
            {assignOrderModalStore.isPersonSelectedFromList ? (
                <UI.Icon
                    className={styles.BinIcon}
                    icon={UI.SVGIcon.Trash}
                    color={ColorStyle("black")}
                    onClick={onClickBinIcon()}
                />
            ) : undefined}
        </div>
    );
};
const ContactDetailsContactForm = observer(ContactDetailsContactFormBase);

const ContactDetailsContactDropdownBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore, ticketStore } = useContext(RootStoreContext);
    const [contactDropdownLabel, setContactDropdownLabel] = useState<string>(
        t("screens.tickets.modals.assign_order.sender").toString()
    );
    const [listOfAllPrimarycontactTypes, setListOfAllPrimarycontactTypes] =
        useState<Map<number | null, { label: string }>>();

    useEffect(() => {
        if (ticketStore.primarycontacttypeEnum) {
            setListOfAllPrimarycontactTypes(getEnumerationMap(ticketStore.primarycontacttypeEnum));
        }
    }, [ticketStore.primarycontacttypeEnum]);

    const getEnumerationMap = (enumerations: Array<GetTicketByNumber_primarycontacttypeEnum>) => {
        const enumerationMap = new Map();

        enumerations.forEach((enumeration) => {
            if (enumeration.key !== null) {
                enumerationMap.set(enumeration.key, {
                    label: enumeration.label
                });
            }
        });

        return enumerationMap;
    };

    const updatePrimaryContact = useCallback(
        (index: number, contactType: { label: string | null; key: string | number | null }) => {
            const person = assignOrderModalStore.filteredIncidentPersons[index];
            if (person) {
                assignOrderModalStore.setPrimaryContactDescription(
                    assignOrderModalStore.getPrimaryContactDescription(person, contactType.label || "")
                );
                assignOrderModalStore.setPrimarycontact(person);

                // Convert key to number if necessary
                const contactTypeKey =
                    typeof contactType.key === "string" ? parseInt(contactType.key, 10) : contactType.key;

                if (contactTypeKey !== null && contactTypeKey !== undefined) {
                    assignOrderModalStore.setPrimarycontacttype(contactTypeKey);
                }
            }
        },
        [assignOrderModalStore]
    );

    const sortedOrderContactDropdownPersons: SortItem[] = useMemo(() => {
        if (!listOfAllPrimarycontactTypes) return [];

        const sortedOrderContactPersons = Array.from(listOfAllPrimarycontactTypes.entries())
            .slice(0, 2)
            .map(([key, value]) => ({
                value: String(key),
                label: value.label || ""
            }));

        // Add the new item to the list
        sortedOrderContactPersons.push({
            value: "3",
            label: t("screens.tickets.form.person.other_person")
        });

        return sortedOrderContactPersons;
    }, [listOfAllPrimarycontactTypes, t]);

    const onSelectDropdown = useCallback(
        (selectedPersonIndex: string) => {
            assignOrderModalStore.setIsOtherPersonSelected(selectedPersonIndex === "3" ? true : false);
            // Find the contact person from the dropdown list whose value matches the selected person's index.
            const selectedContactPersons = sortedOrderContactDropdownPersons.find(
                (contactPerson: SortItem) => contactPerson.value === selectedPersonIndex
            );

            if (selectedContactPersons) {
                assignOrderModalStore.setPrimaryContactDescription("");
                setContactDropdownLabel(selectedContactPersons.label!);

                const index = parseInt(selectedPersonIndex, 10) - 1; // Convert "1" or "2" to 0 or 1
                const contactType = ticketStore.primarycontacttypeEnum[index];

                if (contactType) {
                    updatePrimaryContact(index, {
                        label: contactType.label || "", // Ensure label is a string
                        key: contactType.key // Ensure key is correctly handled
                    });
                }
            }
        },
        [
            assignOrderModalStore,
            sortedOrderContactDropdownPersons,
            setContactDropdownLabel,
            ticketStore.primarycontacttypeEnum,
            updatePrimaryContact
        ]
    );

    return (
        <UI.Dropdown
            id="contactDropdown"
            label={contactDropdownLabel}
            items={sortedOrderContactDropdownPersons}
            onSelect={onSelectDropdown}
        />
    );
};
const ContactDetailsContactDropdown = observer(ContactDetailsContactDropdownBase);

const ContactDetailsPersonSearchBase = () => {
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const onChangeSearchPersonInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputPerson = e.target.value;
            assignOrderModalStore.setCurrentPersonSearchQuery(inputPerson);
        },
        [assignOrderModalStore]
    );

    if (assignOrderModalStore.isOtherPersonSelected || assignOrderModalStore.isPersonSelectedFromList) {
        return (
            <UI.Col md={7}>
                <UI.Input
                    type="text"
                    autoComplete="off"
                    id="serviveOrder-form-contact-input"
                    value={assignOrderModalStore.currentPersonSearchQuery ?? ""}
                    onChange={onChangeSearchPersonInput}
                    iconRight={
                        <UI.Icon icon={UI.SVGIcon.Search} color="grey" size={"small"} style={{ right: "5px" }} />
                    }
                />
                <ContactDetailsPersonSelectionList />
            </UI.Col>
        );
    }
    return null;
};
const ContactDetailsPersonSearch = observer(ContactDetailsPersonSearchBase);

const ContactDetailsPersonSelectionListBase = () => {
    const { assignOrderModalStore } = useContext(RootStoreContext);

    const onClickPerson = useCallback(
        (person: GetPersonsWithoutExternal_persons) => async (e: any) => {
            runInAction(() => {
                assignOrderModalStore.setIsOtherPersonSelected(false);
                assignOrderModalStore.setCurrentPersonSearchQuery("");
                assignOrderModalStore.setPrimarycontacttype(4);
                assignOrderModalStore.setIsPersonSelectedFromList(true);
                assignOrderModalStore.setPrimarycontact(person);
                assignOrderModalStore.setPrimaryContactDescription(
                    assignOrderModalStore.getPrimaryContactDescription(person)
                );
            });
        },
        [assignOrderModalStore]
    );

    if (assignOrderModalStore.currentPersonSearchQuery.length < 3) {
        return null;
    }

    return (
        <div className={styles.PersonsListContainer}>
            {/* List of persons */}
            <div className={styles.PersonsList}>
                {(assignOrderModalStore.currentPersonSearchQuery.length > 2
                    ? assignOrderModalStore.filteredPersons
                    : assignOrderModalStore.personsWithoutExternalList
                ).map((person: GetPersonsWithoutExternal_persons) => (
                    <div key={person.id} className={styles.PersonItem} onClick={onClickPerson(person)}>
                        <div className={styles.PersonDetails}>
                            <p className={styles.PersonName}>
                                {highlightText(
                                    `${person.name1} ${person.name2 || ""}`,
                                    assignOrderModalStore.currentPersonSearchQuery
                                )}
                                {person.email && (
                                    <>
                                        {", "}
                                        {highlightText(person.email, assignOrderModalStore.currentPersonSearchQuery)}
                                    </>
                                )}
                                {person.mobile && (
                                    <>
                                        {", "}
                                        {highlightText(person.mobile, assignOrderModalStore.currentPersonSearchQuery)}
                                    </>
                                )}
                                {person.phoneprivate && (
                                    <>
                                        {", "}
                                        {highlightText(
                                            person.phoneprivate,
                                            assignOrderModalStore.currentPersonSearchQuery
                                        )}
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
const ContactDetailsPersonSelectionList = observer(ContactDetailsPersonSelectionListBase);

const ButtonsBase = () => {
    const { t } = useTranslation();
    const { uiStore, assignOrderModalStore, orderStore } = useContext(RootStoreContext);

    const onClickBack = useCallback(() => {
        assignOrderModalStore.moveToPrevProcessStep();
    }, [assignOrderModalStore]);

    const onClickSendOrder = useCallback(() => {
        let primarycontactId: string = "";
        if (assignOrderModalStore.isOtherPersonSelected) {
            const orderContactPersonData = orderStore.orderContactPerson;

            assignOrderModalStore.insertPersons(orderContactPersonData);

            primarycontactId = orderContactPersonData.id;
            assignOrderModalStore.setPrimarycontacttype(4); // type 4 = other person
        } else {
            const isServiceOrderValid = assignOrderModalStore.validateServiceOrderDescription();

            if (isServiceOrderValid) {
                assignOrderModalStore.createOrder(
                    assignOrderModalStore.isOtherPersonSelected ? primarycontactId : undefined
                );
            }
        }
    }, [assignOrderModalStore, orderStore.orderContactPerson]);

    const serviceProviderHasEmailAddress = assignOrderModalStore.selectedServiceProvider?.email ? true : false;
    return (
        <UI.Row className={`mt-2 d-flex ${uiStore.isMobile ? generalStyles.MobileRowButton : ""}`}>
            <UI.Col>
                <UI.Button
                    className={`ticket-button mt-2 ${generalStyles.BackButton}`}
                    label={t("labels.goback")}
                    variant={"outline-primary"}
                    onClick={onClickBack}
                ></UI.Button>
            </UI.Col>
            <UI.Col>
                <UI.Button
                    className={`ticket-button mt-2 ${generalStyles.NextButton}`}
                    label={t("labels.submit")}
                    onClick={onClickSendOrder}
                    disabled={!serviceProviderHasEmailAddress}
                ></UI.Button>
            </UI.Col>
        </UI.Row>
    );
};
const Buttons = observer(ButtonsBase);

const CreateOrderProgressBase = () => {
    const { t } = useTranslation();
    const { assignOrderModalStore } = useContext(RootStoreContext);
    const { currentOrderCreationStatus } = assignOrderModalStore;

    const onClickFinishCreateOrder = useCallback(() => {
        assignOrderModalStore.setIsModalDisplayed(false);
        assignOrderModalStore.resetAssignOrderModal();
    }, [assignOrderModalStore]);

    if (
        currentOrderCreationStatus !== ORDER_CREATION_STATUS.IN_PROGRESS &&
        currentOrderCreationStatus !== ORDER_CREATION_STATUS.SUCCESS
    ) {
        return null;
    }

    return (
        <UI.Row className={"mt-2 d-flex"}>
            <UI.Col
                xs={12}
                className={`d-flex align-items-center justify-content-center ${styles.CreatingOrderProgressContainer}`}
            >
                <div className="d-flex align-items-center flex-column">
                    <div>
                        {currentOrderCreationStatus === ORDER_CREATION_STATUS.IN_PROGRESS ? (
                            <UI.RotatingSpinner noLogo size={50} className="loading-items-spinner" />
                        ) : null}
                        {currentOrderCreationStatus === ORDER_CREATION_STATUS.SUCCESS ? (
                            <UI.Icon icon={UI.SVGIcon.Check} size={50} color={"black"} />
                        ) : null}
                    </div>
                    <div className="d-flex mt-2">
                        <span className={`${styles.CreatingOrderProgressText} text-center`}>
                            {currentOrderCreationStatus === ORDER_CREATION_STATUS.IN_PROGRESS
                                ? t("screens.tickets.modals.assign_order.order_creation_in_progress").toString()
                                : undefined}
                            {currentOrderCreationStatus === ORDER_CREATION_STATUS.SUCCESS
                                ? t("screens.tickets.modals.assign_order.order_creation_success", {
                                      email: assignOrderModalStore.selectedServiceProvider?.email
                                  }).toString()
                                : undefined}
                        </span>
                    </div>
                </div>
            </UI.Col>
            <UI.Col xs={12} className="mt-4">
                <UI.Button
                    className={`ticket-button mt-2 ${generalStyles.NextButton}`}
                    label={t("labels.finished").toString()}
                    disabled={currentOrderCreationStatus === ORDER_CREATION_STATUS.IN_PROGRESS ? true : false}
                    onClick={onClickFinishCreateOrder}
                ></UI.Button>
            </UI.Col>
        </UI.Row>
    );
};
const CreateOrderProgress = observer(CreateOrderProgressBase);

export const ServiceOrderDescription = Sentry.withProfiler(observer(ServiceOrderDescriptionBase));
